import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarCheck, X } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import BookingLink, { BookingText } from '@/components/BookingLink';

// Routes where a floating "Book a call" CTA would be redundant or in the way.
const HIDDEN_PREFIXES = ['/contact', '/auth', '/admin', '/qualify', '/thank-you', '/success'];

/**
 * Floating "Book a call" CTA that appears once the hero has scrolled out of
 * view (and on any long page). Positioned bottom-left to avoid the
 * bottom-right ScrollToTopButton. Dismissible for the session.
 */
const StickyBookCTA = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reset visibility state on route change so it re-evaluates per page.
  useEffect(() => {
    setVisible(window.scrollY > 600);
  }, [location.pathname]);

  const path = location.pathname.replace(/^\/fr/, '') || '/';
  const hidden = HIDDEN_PREFIXES.some((p) => path.startsWith(p));

  if (hidden || dismissed || !visible) return null;

  return (
    <div className="fixed bottom-6 left-6 z-40 hidden lg:flex items-center gap-1">
      <BookingLink
        location="sticky_floating"
        onClick={() =>
          trackEvent('cta_click', { cta_label: 'Book a Free Consultation', cta_location: 'sticky_floating' })
        }
        className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] transition-all hover:bg-primary/90 hover:shadow-[0_0_25px_hsl(var(--primary)/0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <CalendarCheck className="h-4 w-4" aria-hidden="true" />
        <BookingText />
      </BookingLink>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss booking prompt"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground backdrop-blur transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};

export default StickyBookCTA;
